import React, { useState, useEffect } from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { ChevronLeft, Edit, Plus, Play, CheckCircle, Lock, Layers, ChevronRight, ChevronDown } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';

const ProjectDetail = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [wbsElements, setWbsElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWBSDialog, setShowWBSDialog] = useState(false);
  const [editWBS, setEditWBS] = useState(false);
  const [expandedWBS, setExpandedWBS] = useState(new Set());
  const [wbsProfiles, setWbsProfiles] = useState([]);

  const [wbsFormData, setWbsFormData] = useState({
    wbs_element_id: '', name: '', description: '', project_id: projectId,
    parent_wbs_id: '', level: 1, sort_key: '',
    wbs_profile_id: '', is_account_assignment_element: true,
    is_planning_element: true, is_billing_element: false,
    start_date: '', end_date: '', duration_days: 0
  });

  // eslint-disable-next-line
  useEffect(() => { fetchData(); }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectRes, wbsRes, profilesRes] = await Promise.all([
        psAPI.getProject(projectId),
        psAPI.getWBSElements({ project_id: projectId }),
        psAPI.getWBSProfiles()
      ]);
      setProject(projectRes.data);
      setWbsElements(wbsRes.data);
      setWbsProfiles(profilesRes.data.filter(p => p.status === 'Active'));
    } catch (error) {
      toast.error('Failed to load project');
      navigate('/ps/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    try {
      await psAPI.releaseProject(projectId);
      toast.success('Project released');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleComplete = async () => {
    try {
      await psAPI.completeProject(projectId);
      toast.success('Project completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleClose = async () => {
    try {
      await psAPI.closeProject(projectId);
      toast.success('Project closed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const openWBSDialog = (parentId = null, editElement = null) => {
    if (editElement) {
      setEditWBS(true);
      setWbsFormData({
        ...editElement,
        description: editElement.description || '',
        parent_wbs_id: editElement.parent_wbs_id || '',
        wbs_profile_id: editElement.wbs_profile_id || '',
        start_date: editElement.start_date?.split('T')[0] || '',
        end_date: editElement.end_date?.split('T')[0] || '',
        sort_key: editElement.sort_key || ''
      });
    } else {
      setEditWBS(false);
      const parentLevel = parentId ? wbsElements.find(w => w.wbs_element_id === parentId)?.level || 0 : 0;
      setWbsFormData({
        wbs_element_id: '', name: '', description: '', project_id: projectId,
        parent_wbs_id: parentId || '', level: parentLevel + 1, sort_key: '',
        wbs_profile_id: '', is_account_assignment_element: true,
        is_planning_element: true, is_billing_element: false,
        start_date: project?.start_date?.split('T')[0] || '', 
        end_date: project?.end_date?.split('T')[0] || '', 
        duration_days: 0
      });
    }
    setShowWBSDialog(true);
  };

  const handleWBSSubmit = async () => {
    if (!wbsFormData.wbs_element_id || !wbsFormData.name) {
      toast.error('ID and Name are required');
      return;
    }
    try {
      if (editWBS) {
        await psAPI.updateWBSElement(wbsFormData.wbs_element_id, wbsFormData);
        toast.success('WBS Element updated');
      } else {
        await psAPI.createWBSElement(wbsFormData);
        toast.success('WBS Element created');
      }
      setShowWBSDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleReleaseWBS = async (wbsId) => {
    try {
      await psAPI.releaseWBSElement(wbsId);
      toast.success('WBS Element released');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const toggleWBS = (wbsId) => {
    const newExpanded = new Set(expandedWBS);
    if (newExpanded.has(wbsId)) newExpanded.delete(wbsId);
    else newExpanded.add(wbsId);
    setExpandedWBS(newExpanded);
  };

  const getStatusColor = (status) => {
    const colors = { 'CREATED': 'bg-gray-100 text-gray-800', 'RELEASED': 'bg-orange-100 text-orange-800', 'TECO': 'bg-green-100 text-green-800', 'CLOSED': 'bg-slate-100 text-slate-800' };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const buildWBSTree = (parentId = null) => {
    return wbsElements
      .filter(w => (w.parent_wbs_id || null) === parentId)
      .sort((a, b) => (a.sort_key || '').localeCompare(b.sort_key || '') || a.wbs_element_id.localeCompare(b.wbs_element_id));
  };

  const renderWBSRow = (element, depth = 0) => {
    const children = buildWBSTree(element.wbs_element_id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedWBS.has(element.wbs_element_id);

    return (
      <React.Fragment key={element.wbs_element_id}>
        <TableRow className="hover:bg-gray-50">
          <TableCell>
            <div className="flex items-center" style={{ paddingLeft: `${depth * 24}px` }}>
              {hasChildren ? (
                <Button variant="ghost" size="icon" className="h-6 w-6 mr-1" onClick={() => toggleWBS(element.wbs_element_id)}>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              ) : <span className="w-7" />}
              <Layers className="h-4 w-4 mr-2 text-gray-400" />
              <span className="font-medium">{element.wbs_element_id}</span>
            </div>
          </TableCell>
          <TableCell>{element.name}</TableCell>
          <TableCell>{element.level}</TableCell>
          <TableCell>
            <div className="flex gap-1">
              {element.is_account_assignment_element && <Badge variant="outline" className="text-xs">AA</Badge>}
              {element.is_planning_element && <Badge variant="outline" className="text-xs">Plan</Badge>}
              {element.is_billing_element && <Badge variant="outline" className="text-xs">Bill</Badge>}
            </div>
          </TableCell>
          <TableCell><Badge className={getStatusColor(element.system_status)}>{element.system_status}</Badge></TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openWBSDialog(null, element)} title="Edit">
                <Edit className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openWBSDialog(element.wbs_element_id)} title="Add Child">
                <Plus className="h-3 w-3" />
              </Button>
              {element.system_status === 'CREATED' && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleReleaseWBS(element.wbs_element_id)} title="Release">
                  <Play className="h-3 w-3 text-orange-600" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
        {isExpanded && children.map(child => renderWBSRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading) return <Layout><div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div></div></Layout>;
  if (!project) return <Layout><div className="p-6 text-center text-gray-500">Project not found</div></Layout>;

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="project-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ps/projects')}><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{project.name}</h1>
            <Badge className={getStatusColor(project.system_status)}>{project.system_status}</Badge>
          </div>
          <p className="text-gray-500">{project.project_id}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/ps/projects/${projectId}/edit`)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
          {project.system_status === 'CREATED' && <Button onClick={handleRelease}><Play className="h-4 w-4 mr-2" />Release</Button>}
          {project.system_status === 'RELEASED' && <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700"><CheckCircle className="h-4 w-4 mr-2" />Complete</Button>}
          {project.system_status === 'TECO' && <Button onClick={handleClose} variant="secondary"><Lock className="h-4 w-4 mr-2" />Close</Button>}
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="wbs">WBS Structure ({wbsElements.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Company Code</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{project.company_code_name || project.company_code_id}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Controlling Area</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{project.controlling_area_name || project.controlling_area_id}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Profile</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{project.project_profile_name || project.project_profile_id}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Start Date</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{new Date(project.start_date).toLocaleDateString()}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">End Date</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{new Date(project.end_date).toLocaleDateString()}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Priority</CardTitle></CardHeader>
              <CardContent><p className="font-medium">{project.priority}</p></CardContent>
            </Card>
          </div>
          {project.description && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Description</CardTitle></CardHeader>
              <CardContent><p>{project.description}</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="wbs">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Work Breakdown Structure</CardTitle>
              <Button onClick={() => openWBSDialog()} data-testid="add-wbs-btn"><Plus className="h-4 w-4 mr-2" />Add WBS Element</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WBS Element ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wbsElements.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No WBS elements. Click "Add WBS Element" to create one.</TableCell></TableRow>
                  ) : (
                    buildWBSTree(null).map(element => renderWBSRow(element, 0))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* WBS Dialog */}
      <Dialog open={showWBSDialog} onOpenChange={setShowWBSDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editWBS ? 'Edit' : 'Create'} WBS Element</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>WBS Element ID *</Label>
              <Input value={wbsFormData.wbs_element_id} onChange={(e) => setWbsFormData({...wbsFormData, wbs_element_id: e.target.value.toUpperCase()})} disabled={editWBS} placeholder="e.g., PRJ-001-PHASE1" />
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={wbsFormData.name} onChange={(e) => setWbsFormData({...wbsFormData, name: e.target.value})} placeholder="WBS Element name" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Input value={wbsFormData.description} onChange={(e) => setWbsFormData({...wbsFormData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Parent WBS</Label>
              <Select value={wbsFormData.parent_wbs_id || "__none__"} onValueChange={(v) => setWbsFormData({...wbsFormData, parent_wbs_id: v === "__none__" ? "" : v, level: v === "__none__" ? 1 : (wbsElements.find(w => w.wbs_element_id === v)?.level || 0) + 1})}>
                <SelectTrigger><SelectValue placeholder="None (Top Level)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (Top Level)</SelectItem>
                  {wbsElements.filter(w => w.wbs_element_id !== wbsFormData.wbs_element_id).map(w => <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.wbs_element_id} - {w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>WBS Profile</Label>
              <Select value={wbsFormData.wbs_profile_id || "__none__"} onValueChange={(v) => setWbsFormData({...wbsFormData, wbs_profile_id: v === "__none__" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Select profile" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {wbsProfiles.map(p => <SelectItem key={p.profile_id} value={p.profile_id}>{p.profile_id} - {p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={wbsFormData.start_date} onChange={(e) => setWbsFormData({...wbsFormData, start_date: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={wbsFormData.end_date} onChange={(e) => setWbsFormData({...wbsFormData, end_date: e.target.value})} />
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4 pt-4 border-t">
              <div className="flex items-center justify-between"><Label className="text-sm">Account Assignment</Label><Switch checked={wbsFormData.is_account_assignment_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_account_assignment_element: v})} /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Planning Element</Label><Switch checked={wbsFormData.is_planning_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_planning_element: v})} /></div>
              <div className="flex items-center justify-between"><Label className="text-sm">Billing Element</Label><Switch checked={wbsFormData.is_billing_element} onCheckedChange={(v) => setWbsFormData({...wbsFormData, is_billing_element: v})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWBSDialog(false)}>Cancel</Button>
            <Button onClick={handleWBSSubmit}>{editWBS ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default ProjectDetail;
